import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Users, MapPin, Phone, Ticket, User, Clock,
  ChevronDown, ChevronUp, Anchor, Mail, Edit
} from "lucide-react";

/**
 * Reusable Booking Card Component
 * Used in: Schedule Tab, Bookings Calendar Day Modal
 * 
 * Props:
 * - booking: Booking data object
 * - slotColor: Left border color (optional)
 * - onEdit: Callback when Edit button clicked (optional)
 * - onContact: Callback for contact modal ({ type, value, name })
 * - onOpenTicket: Callback when Ticket button clicked (opens in same screen for iOS compatibility)
 * - showEditButton: Show the Edit button (default: false)
 */

// Format duration
const formatDuration = (minutes) => {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}hr`;
  return `${hours}hr ${mins}min`;
};

// Format time to 12-hour with AM/PM
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  
  // Handle various formats: "09:00", "09:00:00", "9:00 AM"
  // If already has AM/PM, return as-is
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr;
  }
  
  // Parse 24-hour format
  const [hourStr, minuteStr] = timeStr.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr ? minuteStr.substring(0, 2) : '00';
  
  if (isNaN(hour)) return timeStr;
  
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  
  return `${hour}:${minute} ${ampm}`;
};

const BookingCard = ({ 
  booking, 
  slotColor = '#3B82F6',
  onEdit,
  onContact,
  onOpenTicket,
  showEditButton = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const duration = formatDuration(booking.duration_minutes || booking.durationMinutes);
  
  // Build location string
  const locationParts = [booking.city, booking.country].filter(Boolean);
  const locationStr = locationParts.join(', ');
  
  // Check if there's any expandable content
  const hasExpandableContent = 
    booking.asset_name || booking.assetName ||
    booking.team_member_name || booking.teamMemberName ||
    booking.team_assignments?.length > 0 ||
    booking.add_ons?.length > 0 || 
    booking.custom_responses?.length > 0 ||
    booking.waiver_signed !== undefined || booking.waiverSigned !== undefined;

  // Get team display text
  const getTeamDisplay = () => {
    if (booking.team_assignments?.length > 0) {
      const names = booking.team_assignments
        .filter(a => a.teamName)
        .map(a => a.teamName);
      return names.length > 0 ? names.join(', ') : 'Unassigned';
    }
    // Also check teamAssignments (camelCase from API)
    if (booking.teamAssignments?.length > 0) {
      const names = booking.teamAssignments
        .filter(a => a.teamName)
        .map(a => a.teamName);
      return names.length > 0 ? names.join(', ') : 'Unassigned';
    }
    return booking.team_member_name || booking.teamMemberName || 'Unassigned';
  };

  const hasTeamAssigned = 
    (booking.team_assignments?.length > 0 && booking.team_assignments.some(a => a.teamName)) ||
    (booking.teamAssignments?.length > 0 && booking.teamAssignments.some(a => a.teamName)) ||
    booking.team_member_name || booking.teamMemberName;

  // Handle ticket click - navigate in same screen (iOS compatible)
  const handleTicketClick = () => {
    const ticketUrl = booking.ticket_url || booking.ticketUrl;
    if (ticketUrl) {
      // Extract the path from the URL and navigate within app
      try {
        const url = new URL(ticketUrl);
        navigate(url.pathname + url.search);
      } catch {
        // If URL parsing fails, use as-is or call callback
        if (onOpenTicket) {
          onOpenTicket(ticketUrl);
        }
      }
    }
  };

  // Get asset name from either snake_case or camelCase
  const getAssetName = () => {
    return booking.asset_name || booking.assetName || null;
  };

  return (
    <div
      data-testid={`booking-card-${booking.confirmation_code || booking.confirmationCode}`}
      className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] transition-shadow"
      style={{ borderLeft: `4px solid ${slotColor}` }}
    >
      <div className="p-4 sm:p-5 space-y-3">
        {/* ROW 1: Time, Service, Actions */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-sm font-semibold">
                {formatTime12Hour(booking.start_time || booking.startTime)}
              </span>
              <span className="text-xs text-gray-400">({booking.timezone || 'CST'})</span>
              <span className="hidden sm:inline text-gray-300">•</span>
              <h3 className="font-semibold text-gray-900 sm:inline block w-full sm:w-auto mt-1 sm:mt-0">
                {booking.service_name || booking.serviceName}
              </h3>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              <span>{booking.confirmation_code || booking.confirmationCode}</span>
              {duration && (
                <>
                  <span className="text-gray-300">•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {duration}
                  </span>
                </>
              )}
              <span className="text-gray-300">•</span>
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {booking.party_size || booking.partySize || 1} guest{(booking.party_size || booking.partySize || 1) !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          {/* Action buttons - Desktop: inline, Mobile: stacked */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Ticket button - opens in same screen for iOS */}
            {(booking.ticket_url || booking.ticketUrl) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTicketClick}
                className="h-9 gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100"
                data-testid="booking-card-ticket-btn"
              >
                <Ticket className="w-4 h-4" />
                <span className="hidden sm:inline">Ticket</span>
              </Button>
            )}
            {showEditButton && onEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(booking)}
                className="h-9 gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200"
                data-testid="booking-card-edit-btn"
              >
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            )}
          </div>
        </div>

        {/* ROW 2: Customer Name + Status Badges + Contact Icons */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <User className="w-3.5 h-3.5 text-gray-500" />
            </div>
            <span className="font-medium text-gray-900 truncate">
              {booking.customer_name || booking.customerName}
            </span>
            
            {/* Booking Status Badge - Show if cancelled or special status */}
            {(booking.status === 'cancelled' || booking.status === 'checked_in') && (
              <span 
                className={`text-xs px-2 py-0.5 rounded-md border font-normal ${
                  booking.status === 'cancelled' 
                    ? 'bg-red-50 border-red-200 text-red-700' 
                    : 'bg-green-50 border-green-200 text-green-700'
                }`}
              >
                {booking.status === 'cancelled' ? 'Cancelled' : 'Checked In'}
              </span>
            )}
            
            {/* Payment Status Badge - Color coded for operator decision making */}
            {(booking.payment_status || booking.paymentStatus) && (
              <span 
                className={`text-xs px-2 py-0.5 rounded-md border font-normal ${
                  (booking.payment_status || booking.paymentStatus) === 'paid' 
                    ? 'bg-green-50 border-green-200 text-green-700' 
                    : (booking.payment_status || booking.paymentStatus) === 'partial'
                    ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                    : (booking.payment_status || booking.paymentStatus) === 'refunded'
                    ? 'bg-gray-50 border-gray-200 text-gray-600'
                    : (booking.payment_status || booking.paymentStatus) === 'partially_refunded'
                    ? 'bg-orange-50 border-orange-200 text-orange-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                }`}
              >
                {(booking.payment_status || booking.paymentStatus) === 'paid' ? '✓ Paid' 
                  : (booking.payment_status || booking.paymentStatus) === 'partial' ? '⚠ Partial'
                  : (booking.payment_status || booking.paymentStatus) === 'refunded' ? 'Refunded'
                  : (booking.payment_status || booking.paymentStatus) === 'partially_refunded' ? 'Part Refund'
                  : (booking.payment_status || booking.paymentStatus) === 'pending' ? 'Unpaid'
                  : 'Unpaid'}
              </span>
            )}
            
            {/* Waiver status - DESKTOP ONLY */}
            <span className="hidden sm:inline text-sm text-gray-400">
              · Waiver {(booking.waiver_signed || booking.waiverSigned) ? 'Signed' : 'Pending'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {(booking.customer_phone || booking.customerPhone) && onContact && (
              <button 
                onClick={() => onContact({ 
                  type: 'phone', 
                  value: booking.customer_phone || booking.customerPhone,
                  name: booking.customer_name || booking.customerName,
                  booking: booking
                })}
                className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-gray-100 hover:bg-green-50 hover:text-green-600 flex items-center justify-center text-gray-500 transition-colors"
              >
                <Phone className="w-4 h-4" />
              </button>
            )}
            {(booking.customer_email || booking.customerEmail) && onContact && (
              <button 
                onClick={() => onContact({ 
                  type: 'email', 
                  value: booking.customer_email || booking.customerEmail,
                  name: booking.customer_name || booking.customerName,
                  booking: booking
                })}
                className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-gray-100 hover:bg-blue-50 hover:text-blue-600 flex items-center justify-center text-gray-500 transition-colors"
              >
                <Mail className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* ROW 3: Asset + Team - DESKTOP ONLY */}
        <div className="hidden sm:flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Anchor className="w-4 h-4 text-gray-400" />
            <span className={getAssetName() ? 'text-gray-900' : 'text-gray-400'}>
              {getAssetName() || 'Unassigned'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className={hasTeamAssigned ? 'text-gray-900' : 'text-gray-400'}>
              {getTeamDisplay()}
            </span>
          </div>
        </div>

        {/* ROW 4: Location + Meeting Point */}
        {(locationStr || booking.meeting_point || booking.pickupLocation) && (
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <span>
              {locationStr && <span className="text-gray-900">{locationStr}</span>}
              {locationStr && (booking.meeting_point || booking.pickupLocation) && <span className="text-gray-400"> · </span>}
              {(booking.meeting_point || booking.pickupLocation) && (
                <span className="text-gray-400">{booking.meeting_point || booking.pickupLocation}</span>
              )}
            </span>
          </div>
        )}

        {/* DESKTOP: Extras toggle (add-ons & responses only) */}
        {(() => {
          const addOns = booking.add_ons || booking.selectedAddOns || [];
          const responses = booking.custom_responses || booking.customAnswers || [];
          const hasExtras = addOns.length > 0 || responses.length > 0;
          
          if (!hasExtras) return null;
          
          return (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="hidden sm:flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              {addOns.length > 0 && `${addOns.length} add-on${addOns.length > 1 ? 's' : ''}`}
              {addOns.length > 0 && responses.length > 0 && ', '}
              {responses.length > 0 && `${responses.length} response${responses.length > 1 ? 's' : ''}`}
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          );
        })()}

        {/* MOBILE: See more toggle */}
        {hasExpandableContent && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="sm:hidden flex items-center gap-1 text-sm text-blue-600 font-medium"
          >
            {isExpanded ? 'See less' : 'See more'}
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
      </div>
      
      {/* EXPANDED SECTION */}
      {isExpanded && (
        <div className="px-4 sm:px-5 py-4 bg-gray-50 space-y-4">
          {/* MOBILE ONLY: Waiver Status */}
          <div className="sm:hidden">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Waiver</p>
            <Badge variant={(booking.waiver_signed || booking.waiverSigned) ? "default" : "secondary"} className="text-xs">
              {(booking.waiver_signed || booking.waiverSigned) ? '✓ Signed' : 'Pending'}
            </Badge>
          </div>

          {/* MOBILE ONLY: Asset & Team Assignments */}
          {(getAssetName() || booking.team_member_name || booking.teamMemberName || booking.team_assignments?.length > 0 || booking.teamAssignments?.length > 0) && (
            <div className="sm:hidden">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Assignments</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Anchor className="w-4 h-4 text-gray-400" />
                  <span className={getAssetName() ? 'text-gray-900' : 'text-gray-400'}>
                    {getAssetName() || 'No asset assigned'}
                  </span>
                </div>
                {(booking.team_assignments?.length > 0 || booking.teamAssignments?.length > 0) ? (
                  (booking.team_assignments || booking.teamAssignments).map((assignment, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500 text-xs">{assignment.roleName}:</span>
                      <span className={assignment.teamName ? 'text-gray-900' : 'text-gray-400'}>
                        {assignment.teamName || 'Unassigned'}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className={(booking.team_member_name || booking.teamMemberName) ? 'text-gray-900' : 'text-gray-400'}>
                      {booking.team_member_name || booking.teamMemberName || 'No team member assigned'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add-ons */}
          {(() => {
            const addOns = booking.add_ons || booking.selectedAddOns || [];
            if (addOns.length === 0) return null;
            
            return (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Add-ons</p>
                <div className="flex flex-wrap gap-2">
                  {addOns.map((addon, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {addon.quantity > 1 && `${addon.quantity}× `}{addon.name}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })()}
          
          {/* Custom Responses */}
          {(() => {
            const responses = booking.custom_responses || booking.customAnswers || [];
            if (responses.length === 0) return null;
            
            return (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Responses</p>
                <div className="space-y-2">
                  {responses.map((resp, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="text-gray-500">{resp.question}:</span>{' '}
                      <span className="text-gray-900 font-medium">{resp.answer}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default BookingCard;
